import { Group, Badge } from '@mantine/core';
import { BsX } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { queryParams } from 'utils/query';
import { formatDate } from 'utils/dates';
import { TableConfig } from 'models/general';
import { FilterBadge } from 'models/filter';
import { listQueryParameterValues } from 'utils/helpers';

interface Props {
  config: TableConfig;
  badges: FilterBadge[];
  datesKey?: string;
}

export const ActiveFiltersBar = ({ config, badges, datesKey }: Props) => {
  const { queryKey, defaultFilters } = config;
  const { t } = useTranslation();
  const { setParameters, parameters: filters } = useSearchParameters(queryKey);

  const handleBadgeClick = (queryParameter: string) => {
    if (queryParameter === queryParams.betweenDates) {
      setParameters({
        ...filters,
        [queryParams.startDate]: '',
        [queryParams.endDate]: '',
      });
      return;
    }
    setParameters({ ...filters, [queryParameter]: '' });
  };

  const renderBadge = (queryParameter: string, label: string, value: string) => {
    return (
      value !== defaultFilters?.[queryParameter] && (
        <Badge
          color={'var(--mantine-color-gray-7)'}
          key={queryParameter}
          variant={'outline'}
          style={{ cursor: 'pointer' }}
          onClick={() => handleBadgeClick(queryParameter)}
          rightSection={<BsX />}
        >
          {`${label}: ${value}`}
        </Badge>
      )
    );
  };

  const renderDatesBadge = () => {
    if (!datesKey) return null;

    if (filters[queryParams.startDate] && filters[queryParams.endDate]) {
      return renderBadge(
        queryParams.betweenDates,
        `${t(datesKey)} ${t('badge_between')}`,
        `${formatDate({ timestamp: +filters[queryParams.startDate] })} - ${formatDate({ timestamp: +filters[queryParams.endDate] })}`,
      );
    }

    if (filters[queryParams.startDate]) {
      return renderBadge(
        queryParams.startDate,
        `${t(datesKey)} ${t('badge_after')}`,
        formatDate({ timestamp: +filters[queryParams.startDate] }),
      );
    }

    if (filters[queryParams.endDate]) {
      return renderBadge(
        queryParams.endDate,
        `${t(datesKey)} ${t('badge_before')}`,
        formatDate({ timestamp: +filters[queryParams.endDate] }),
      );
    }
    return null;
  };

  return (
    <Group gap={'xs'}>
      {renderDatesBadge()}
      {badges.map((badge) => {
        const value = filters[badge.queryParameter];
        return (
          value && renderBadge(badge.queryParameter, badge.label, listQueryParameterValues(value, badge.renderValue))
        );
      })}
    </Group>
  );
};
